import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { CheckBeforeSave } from "@coachbrain/contracts";
import { Card, Chip, LayerChip, PrimaryButton, SectionLabel, TapAnswers, Num, Columns, Col } from "@coachbrain/ui";
import { CoachShell, ErrorBox, Loading } from "../components/common";
import { useLoad, useOnline, useUnits } from "../lib/hooks";
import { enqueueCapture, listQueued, removeCapture, syncQueued } from "../lib/offline";
import { prescriptionText } from "../lib/format";
import { en } from "../i18n/en";

export function CaptureRoute() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const units = useUnits();
  const online = useOnline();
  const { api, data: plan } = useLoad((a) => a.plannedForClient(id), [id]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLInputElement>(null);

  useEffect(() => { void listQueued().then((q) => setQueued(q.length)); }, []);
  useEffect(() => {
    if (!api || !online) return;
    void syncQueued(async (q) => { const c = await api.createCapture(q.clientId, q.input); void navigate({ to: `/clients/${q.clientId}/check/${c.captureId}` }); }).then(() => listQueued().then((q) => setQueued(q.length)));
  }, [api, online, navigate]);

  const submit = async (input: { kind: "text" | "photo" | "audio"; text: string; blobDataUrl?: string; sameAsPlanned?: boolean }) => {
    if (!api) return;
    setBusy(true); setErr(null);
    const q = await enqueueCapture(id, input); // persisted before any network (R-CAP-06)
    try {
      if (!online) { setQueued((n) => n + 1); setBusy(false); return; }
      const check = await api.createCapture(id, q.input);
      await removeCapture(q.id);
      void navigate({ to: `/clients/${id}/check/${check.captureId}` });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setQueued((n) => n + 1); }
    finally { setBusy(false); }
  };
  const readFile = (f: File) => new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(f); });

  return (
    <CoachShell title={en.capture.title} subtitle={plan ? `${plan.title}` : undefined} right={`${en.nav.log}`}>
      {!online ? <Card tone="unresolved"><span className="cb-strong">{en.capture.offline}</span><span className="cb-small">{en.capture.queued}</span></Card> : null}
      {queued > 0 ? <Card tone="unresolved"><span className="cb-small">{queued} capture{queued === 1 ? "" : "s"} waiting to sync</span></Card> : null}
      <Columns kind="two">
      <Col area="left">
      <Card>
        <div className="cb-field">
          <label htmlFor="dictation">What happened</label>
          <textarea id="dictation" className="cb-textarea" placeholder={en.capture.placeholder} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="cb-inline-actions">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="cb-sr-only" aria-label={en.capture.photo} onChange={async (e) => { const f = e.target.files?.[0]; if (f) await submit({ kind: "photo", text: text || `[photo: ${f.name}]`, blobDataUrl: await readFile(f) }); }} />
          <input ref={audioRef} type="file" accept="audio/*" capture className="cb-sr-only" aria-label={en.capture.voice} onChange={async (e) => { const f = e.target.files?.[0]; if (f) await submit({ kind: "audio", text: text || `[voice memo: ${f.name}]`, blobDataUrl: await readFile(f) }); }} />
          <Chip tone="ground" onClick={() => fileRef.current?.click()}>📷 {en.capture.photo}</Chip>
          <Chip tone="ground" onClick={() => audioRef.current?.click()}>🎙 {en.capture.voice}</Chip>
        </div>
        <PrimaryButton tone="navy" full disabled={busy || !text.trim()} onClick={() => void submit({ kind: "text", text })}>{busy ? en.common.loading : en.capture.submit}</PrimaryButton>
        {err ? <span className="cb-error cb-small">{err}</span> : null}
      </Card>
      </Col>
      <Col area="right">
      {plan ? (
        <Card className="cb-card-flush" style={{ opacity: 0.85 }}>
          <div><SectionLabel right={<Chip tone="navy" onClick={() => void submit({ kind: "text", text: "same as planned", sameAsPlanned: true })}>{en.capture.sameAsPlanned}</Chip>}>{en.capture.planned}</SectionLabel></div>
          {plan.blocks.flatMap((b) => b.exercises).map((ex) => <div key={ex.id} className="cb-row-main cb-muted"><span>{ex.exerciseText}</span><Num>{prescriptionText(ex.prescription, units)}</Num></div>)}
        </Card>
      ) : null}
      </Col>
      </Columns>
    </CoachShell>
  );
}

export function CheckRoute() {
  const { id, captureId } = useParams({ strict: false }) as { id: string; captureId: string };
  const navigate = useNavigate();
  const { api, data, error, reload } = useLoad((a) => a.getCapture(captureId), [captureId]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [sens, setSens] = useState<Record<string, "discomfort" | "pain">>({});
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  if (error) return <CoachShell><ErrorBox message={error} onRetry={reload} /></CoachShell>;
  if (!data || !api) return <CoachShell><Loading /></CoachShell>;
  const c: CheckBeforeSave = data;
  const t = en.check;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.saveCapture(c.captureId, {
        answers: Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer })),
        sensations: Object.entries(sens).map(([entryId, kind]) => ({ entryId, kind })),
        confirmedInferences: c.inferences.filter((i) => !corrections[i.id]).map((i) => i.id),
        correctedInferences: Object.entries(corrections).map(([id2, correction]) => ({ id: id2, correction })),
      });
      void navigate({ to: `/clients/${id}/review/${r.reviewId}` });
    } finally { setBusy(false); }
  };
  return (
    <CoachShell title={t.title} subtitle={t.header(c.header.questions, c.header.toConfirm)} right="Fred · Thu 4 Sep" hideNav>
      <Columns kind="two">
      <Col area="left">
      <Card><SectionLabel>{t.dictated}</SectionLabel><div className="cb-dictated">{c.dictated}</div></Card>
      <Card className="cb-card-flush cb-desktop-only">
        <div><SectionLabel>{t.facts}</SectionLabel></div>
        {c.facts.map((f) => <div key={f.entryId} className="cb-row-main"><span className="cb-row-left">{f.label}</span>{/skipped/i.test(f.summary) ? <span className="cb-strong cb-text-unresolved">{f.summary}</span> : <Num className="cb-row-right">{f.summary}</Num>}</div>)}
      </Card>
      </Col>
      <Col area="right">
      {c.ambiguities.map((a) => (
        <Card key={a.questionId} tone="unresolved">
          <SectionLabel tone="unresolved">{t.ambiguous}</SectionLabel>
          <div className="cb-h2">{a.text}</div>
          <TapAnswers options={a.options} picked={answers[a.questionId] ?? null} onPick={(o) => setAnswers((s) => ({ ...s, [a.questionId]: o }))} />
          <div className="cb-small cb-text-unresolved">{t.savingOpen}</div>
        </Card>
      ))}
      {c.sensations.map((s) => (
        <Card key={s.entryId} accent="navy">
          <SectionLabel>{t.sensation}</SectionLabel>
          <div className="cb-h2">“{s.text}”</div>
          <div className="cb-row-sub" style={{ color: "var(--cb-text)" }}>{t.locationReport}</div>
          <div className="cb-tap-row">
            {(["discomfort", "pain"] as const).map((k) => <button key={k} type="button" className={`cb-tap cb-tap-ground ${sens[s.entryId] === k ? "cb-tap-primary" : ""}`} aria-pressed={sens[s.entryId] === k} onClick={() => setSens((x) => ({ ...x, [s.entryId]: k }))}>{k === "pain" ? t.pain : t.discomfort}</button>)}
          </div>
        </Card>
      ))}
      {c.inferences.length ? (
        <Card tone="provisional">
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}><LayerChip layer="interpretation" /><span className="cb-strong cb-text-violet cb-small">{t.confirmOrCorrect}</span></div>
          <ul className="cb-bullets cb-bullets-violet">
            {c.inferences.map((i) => (
              <li key={i.id}>
                <span dangerouslySetInnerHTML={{ __html: i.text.replace(/\b(teaching|working set)\b/g, "<strong>$1</strong>") }} />
                {corrections[i.id] !== undefined ? <input className="cb-input" style={{ marginTop: 6 }} placeholder="Correction" value={corrections[i.id]} onChange={(e) => setCorrections((s) => ({ ...s, [i.id]: e.target.value }))} /> : <button type="button" className="cb-link-button cb-small" style={{ marginLeft: 8 }} onClick={() => setCorrections((s) => ({ ...s, [i.id]: "" }))}>correct</button>}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Card className="cb-card-flush cb-phone-only">
        <div><SectionLabel>{t.facts}</SectionLabel></div>
        {c.facts.map((f) => <div key={f.entryId} className="cb-row-main"><span className="cb-row-left">{f.label}</span>{/skipped/i.test(f.summary) ? <span className="cb-strong cb-text-unresolved">{f.summary}</span> : <Num className="cb-row-right">{f.summary}</Num>}</div>)}
      </Card>
      <PrimaryButton tone="emerald" full disabled={busy} onClick={() => void save()}>{t.save}</PrimaryButton>
      <p className="cb-note" style={{ textAlign: "center" }}>{t.footer}</p>
      </Col>
      </Columns>
    </CoachShell>
  );
}
